import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ActivityEntry, AnalysisVersion, Goals, Meal, MealCategory, MealDraft, Profile, UserAccount } from './types'
import { getLocalDate, recommendGoals } from './lib/nutrition'
import { api } from './api/client'
import { getImage } from './lib/db'

const nowIso = () => new Date().toISOString()
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

function emptyDraft(category: MealCategory | null = null): MealDraft {
  return {
    id: crypto.randomUUID(),
    category,
    sourceType: null,
    imageKey: null,
    imageHash: null,
    imageWidth: null,
    imageHeight: null,
    imageBytes: null,
    userDescription: '',
    productName: '',
    productBrand: '',
    productVariant: '',
    packageSize: '',
    createdAt: nowIso(),
  }
}

interface MarlovaState {
  runtimeMode: 'api' | 'demo'
  sessionChecked: boolean
  sessionChecking: boolean
  account: UserAccount | null
  authenticated: boolean
  profile: Profile | null
  goals: Goals | null
  meals: Meal[]
  activities: ActivityEntry[]
  draft: MealDraft
  register: (email: string, password: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  bootstrapSession: () => Promise<void>
  hydrateServerState: () => Promise<void>
  loadDemo: () => void
  saveProfile: (profile: Profile) => Promise<void>
  saveGoals: (goals: Goals) => Promise<void>
  saveHand: (values: Pick<Profile, 'handLengthMm' | 'palmWidthMm' | 'handSetupSkipped'>) => Promise<void>
  setOnboardingStep: (step: UserAccount['onboardingStep']) => Promise<void>
  updateDraft: (values: Partial<MealDraft>) => void
  resetDraft: (category?: MealCategory | null) => void
  createMealFromDraft: () => Promise<string>
  refreshMeal: (mealId: string) => Promise<void>
  confirmProductMatch: (mealId: string, analysisId: string, productMatchId: string) => Promise<void>
  setMealAnalysis: (mealId: string, analysis: AnalysisVersion) => void
  setMealStatus: (mealId: string, status: Meal['status']) => void
  confirmMeal: (mealId: string, analysisId: string) => Promise<void>
  updateMeal: (mealId: string, values: Partial<Pick<Meal, 'category' | 'currentDescription'>>) => Promise<void>
  deleteMeal: (mealId: string) => Promise<void>
  deleteMealImage: (mealId: string) => Promise<void>
  addActivity: (activity: Omit<ActivityEntry, 'id'>) => Promise<void>
  updateActivity: (activityId: string, values: Omit<ActivityEntry, 'id'>) => Promise<void>
  deleteActivity: (activityId: string) => Promise<void>
  exportAccount: () => Promise<object>
  deleteAccount: (password?: string) => Promise<void>
}

function demoAnalysis(description: string, totals: AnalysisVersion['totals'], version = 1): AnalysisVersion {
  const id = crypto.randomUUID()
  return {
    id,
    version,
    type: 'initial',
    description,
    totals,
    range: { caloriesMin: Math.round(totals.calories * 0.88), caloriesMax: Math.round(totals.calories * 1.14) },
    confidence: 'moderate',
    confidenceReasons: ['Full plate visible', 'Description reviewed'],
    components: [{
      id: crypto.randomUUID(),
      name: description,
      quantity: '1 serving',
      estimatedGrams: 340,
      preparation: 'prepared',
      sourceType: 'estimated',
      nutrition: totals,
      confidence: 'moderate',
    }],
    assumptions: ['Typical preparation and serving amount'],
    clarificationQuestion: null,
    productMatches: [],
    promptVersion: 'meal-analysis-v1',
    schemaVersion: 1,
    model: 'demo-provider-v1',
    createdAt: nowIso(),
  }
}

function demoMeal(category: MealCategory, description: string, totals: AnalysisVersion['totals'], hour: number): Meal {
  const analysis = demoAnalysis(description, totals)
  const date = getLocalDate()
  const loggedAt = new Date()
  loggedAt.setHours(hour, 15, 0, 0)
  return {
    id: crypto.randomUUID(), category, sourceType: 'prepared', status: 'confirmed', loggedAt: loggedAt.toISOString(),
    localDate: date, timezone, userDescription: description, currentDescription: description,
    productName: '', productBrand: '', productVariant: '', packageSize: '', imageKey: null,
    analyses: [analysis], currentAnalysisId: analysis.id, confirmedAnalysisId: analysis.id, createdAt: loggedAt.toISOString(),
  }
}

const demoProfile: Profile = {
  dateOfBirth: '1994-04-18', sexForEnergy: 'female', heightCm: 165, weightKg: 62,
  activityLevel: 'moderate', preferredUnits: 'metric', handLengthMm: 178, palmWidthMm: 82,
  handSetupSkipped: false, timezone,
}

export const useMarlovaStore = create<MarlovaState>()(persist((set, get) => ({
  runtimeMode: 'api',
  sessionChecked: false,
  sessionChecking: false,
  account: null,
  authenticated: false,
  profile: null,
  goals: null,
  meals: [],
  activities: [],
  draft: emptyDraft(),
  register: async (email, password) => {
    const account = await api.register(email, password)
    set({ runtimeMode: 'api', sessionChecked: true, account, authenticated: true, profile: null, goals: null, meals: [], activities: [] })
  },
  login: async (email, password) => {
    const account = await api.login(email, password)
    set({ runtimeMode: 'api', sessionChecked: true, account, authenticated: true })
    await get().hydrateServerState()
  },
  logout: async () => {
    if (get().runtimeMode === 'api') await api.logout()
    set({ account: null, authenticated: false, profile: null, goals: null, meals: [], activities: [], sessionChecked: true })
  },
  bootstrapSession: async () => {
    if (get().sessionChecked || get().sessionChecking) return
    set({ sessionChecking: true })
    if (get().runtimeMode === 'demo') { set({ sessionChecked: true, sessionChecking: false }); return }
    const publicPath = ['/welcome', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].some((path) => window.location.pathname.startsWith(path))
    if (publicPath) { set({ sessionChecked: true, sessionChecking: false }); return }
    try {
      const account = await api.me()
      set({ account, authenticated: true })
      await get().hydrateServerState()
    } catch {
      set({ account: null, authenticated: false, profile: null, goals: null, meals: [], activities: [] })
    } finally {
      set({ sessionChecked: true, sessionChecking: false })
    }
  },
  hydrateServerState: async () => {
    if (get().runtimeMode !== 'api' || !get().authenticated) return
    const [profile, goals, meals, day] = await Promise.all([
      api.getProfile().catch(() => null),
      api.getGoals().catch(() => null),
      api.listMeals().catch(() => []),
      api.getDay(getLocalDate()).catch(() => ({ meals: [], activities: [] })),
    ])
    const mergedMeals = [...day.meals, ...meals.filter((meal) => !day.meals.some((current) => current.id === meal.id))]
    set({ profile, goals, meals: mergedMeals, activities: day.activities })
  },
  loadDemo: () => {
    const goals = recommendGoals(demoProfile)
    goals.baseCalories = 2_050
    goals.proteinG = 100
    set({
      runtimeMode: 'demo',
      sessionChecked: true,
      account: { id: crypto.randomUUID(), email: 'demo@marlova.app', emailVerified: true, onboardingStep: 'completed' },
      authenticated: true,
      profile: demoProfile,
      goals,
      meals: [
        demoMeal('breakfast', 'Greek yogurt, berries and granola', { calories: 410, proteinG: 25, carbohydratesG: 52, fatG: 12 }, 8),
        demoMeal('lunch', 'Salmon rice bowl with vegetables', { calories: 635, proteinG: 39, carbohydratesG: 74, fatG: 21 }, 12),
      ],
      activities: [{
        id: crypto.randomUUID(), name: 'Evening walk', caloriesBurned: 180, durationMinutes: 42, notes: '',
        occurredAt: nowIso(), localDate: getLocalDate(), affectsDailyTarget: true,
      }],
      draft: emptyDraft(),
    })
  },
  saveProfile: async (profile) => {
    const saved = get().runtimeMode === 'api' ? await api.updateProfile(profile) : profile
    set((state) => ({ profile: saved, account: state.account ? { ...state.account, onboardingStep: state.account.onboardingStep === 'completed' ? 'completed' : 'goals' } : null }))
  },
  saveGoals: async (goals) => {
    const saved = get().runtimeMode === 'api' ? await api.updateGoals(goals) : goals
    set((state) => ({ goals: saved, account: state.account ? { ...state.account, onboardingStep: state.account.onboardingStep === 'completed' ? 'completed' : 'hand' } : null }))
  },
  saveHand: async (values) => {
    const profile = get().profile ? { ...get().profile!, ...values } : null
    const saved = profile && get().runtimeMode === 'api' ? await api.updateProfile(profile) : profile
    set((state) => ({ profile: saved, account: state.account ? { ...state.account, onboardingStep: state.account.onboardingStep === 'completed' ? 'completed' : 'tutorial' } : null }))
  },
  setOnboardingStep: async (onboardingStep) => {
    if (get().runtimeMode === 'api' && onboardingStep === 'completed') await api.completeOnboarding()
    set((state) => ({ account: state.account ? { ...state.account, onboardingStep } : null }))
  },
  updateDraft: (values) => set((state) => ({ draft: { ...state.draft, ...values } })),
  resetDraft: (category = null) => set({ draft: emptyDraft(category) }),
  createMealFromDraft: async () => {
    const draft = get().draft
    if (get().runtimeMode === 'api') {
      let meal = await api.createMeal({
        category: draft.category ?? 'snack',
        sourceType: draft.sourceType ?? 'prepared',
        loggedAt: nowIso(),
        timezone,
        userDescription: draft.userDescription || null,
        product: { name: draft.productName || null, brand: draft.productBrand || null, variant: draft.productVariant || null, packageSize: draft.packageSize || null },
      })
      const localImage = draft.imageKey ? await getImage(draft.imageKey) : null
      if (!localImage || !draft.imageHash) throw new Error('The processed image is no longer available on this device.')
      meal = await api.uploadMealImage(meal.id, localImage.blob, draft.imageHash)
      meal = { ...meal, status: 'analyzing', imageKey: draft.imageKey }
      set((state) => ({ meals: [meal, ...state.meals.filter((item) => item.id !== meal.id)] }))
      return meal.id
    }
    const id = crypto.randomUUID()
    const meal: Meal = {
      id,
      category: draft.category ?? 'snack',
      sourceType: draft.sourceType ?? 'prepared',
      status: 'analyzing',
      loggedAt: nowIso(),
      localDate: getLocalDate(),
      timezone,
      userDescription: draft.userDescription,
      currentDescription: draft.userDescription,
      productName: draft.productName,
      productBrand: draft.productBrand,
      productVariant: draft.productVariant,
      packageSize: draft.packageSize,
      imageKey: draft.imageKey,
      analyses: [],
      currentAnalysisId: null,
      confirmedAnalysisId: null,
      createdAt: nowIso(),
    }
    set((state) => ({ meals: [meal, ...state.meals] }))
    return id
  },
  refreshMeal: async (mealId) => {
    if (get().runtimeMode !== 'api') return
    const current = get().meals.find((meal) => meal.id === mealId)
    const refreshed = await api.getMeal(mealId)
    if (current?.imageKey && !current.imageKey.startsWith('/api/')) refreshed.imageKey = current.imageKey
    set((state) => ({
      meals: state.meals.some((meal) => meal.id === mealId)
        ? state.meals.map((meal) => meal.id === mealId ? refreshed : meal)
        : [refreshed, ...state.meals],
    }))
  },
  confirmProductMatch: async (mealId, analysisId, productMatchId) => {
    if (get().runtimeMode === 'api') await api.confirmProductMatch(mealId, productMatchId)
    set((state) => ({ meals: state.meals.map((meal) => meal.id !== mealId ? meal : {
      ...meal,
      analyses: meal.analyses.map((analysis) => analysis.id !== analysisId ? analysis : {
        ...analysis,
        productMatches: analysis.productMatches.map((match) => ({ ...match, confirmedByUser: match.id === productMatchId })),
      }),
    }) }))
  },
  setMealAnalysis: (mealId, analysis) => set((state) => ({
    meals: state.meals.map((meal) => meal.id === mealId ? {
      ...meal,
      status: 'needs_review',
      analyses: [...meal.analyses, analysis],
      currentAnalysisId: analysis.id,
      currentDescription: analysis.description,
    } : meal),
  })),
  setMealStatus: (mealId, status) => set((state) => ({
    meals: state.meals.map((meal) => meal.id === mealId ? { ...meal, status } : meal),
  })),
  confirmMeal: async (mealId, analysisId) => {
    if (get().runtimeMode === 'api') await api.confirmMeal(mealId, analysisId)
    set((state) => ({ meals: state.meals.map((meal) => meal.id === mealId ? { ...meal, status: 'confirmed', currentAnalysisId: analysisId, confirmedAnalysisId: analysisId } : meal) }))
  },
  updateMeal: async (mealId, values) => {
    const current = get().meals.find((meal) => meal.id === mealId)
    if (get().runtimeMode === 'api' && values.category && values.category !== current?.category) await api.updateMealCategory(mealId, values.category)
    set((state) => ({ meals: state.meals.map((meal) => meal.id === mealId ? { ...meal, ...values } : meal) }))
  },
  deleteMeal: async (mealId) => {
    if (get().runtimeMode === 'api') await api.deleteMeal(mealId)
    set((state) => ({ meals: state.meals.filter((meal) => meal.id !== mealId) }))
  },
  deleteMealImage: async (mealId) => {
    if (get().runtimeMode === 'api') await api.deleteMealImage(mealId)
    set((state) => ({ meals: state.meals.map((meal) => meal.id === mealId ? { ...meal, imageKey: null } : meal) }))
  },
  addActivity: async (activity) => {
    const saved = get().runtimeMode === 'api' ? await api.addActivity(activity, get().profile?.timezone ?? timezone) : { ...activity, id: crypto.randomUUID() }
    set((state) => ({ activities: [saved, ...state.activities] }))
  },
  updateActivity: async (activityId, values) => {
    const saved = get().runtimeMode === 'api'
      ? await api.updateActivity(activityId, values, get().profile?.timezone ?? timezone)
      : { ...values, id: activityId }
    set((state) => ({
      activities: state.activities.map((activity) => activity.id === activityId ? saved : activity),
    }))
  },
  deleteActivity: async (activityId) => {
    if (get().runtimeMode === 'api') await api.deleteActivity(activityId)
    set((state) => ({ activities: state.activities.filter((activity) => activity.id !== activityId) }))
  },
  exportAccount: async () => get().runtimeMode === 'api' ? api.exportAccount() : ({ exportedAt: nowIso(), profile: get().profile, goals: get().goals, meals: get().meals, activities: get().activities }),
  deleteAccount: async (password = '') => {
    if (get().runtimeMode === 'api') await api.deleteAccount(password)
    set({ account: null, authenticated: false, profile: null, goals: null, meals: [], activities: [], draft: emptyDraft(), sessionChecked: true })
  },
}), {
  name: 'marlova-state-v1',
  version: 1,
  partialize: (state) => state.runtimeMode === 'demo' ? ({
    runtimeMode: state.runtimeMode,
    sessionChecked: state.sessionChecked,
    account: state.account,
    authenticated: state.authenticated,
    profile: state.profile,
    goals: state.goals,
    meals: state.meals,
    activities: state.activities,
    draft: state.draft,
  }) : ({ runtimeMode: 'api' as const, draft: state.draft }),
}))
